/**
 * Create / update an estimate together with its line items.
 *
 * `estimate_number` is assigned by the `trg_estimates_generate_number`
 * trigger on insert and is never computed here. Line items are replaced
 * (delete, then insert) on update; every step reports its own failure so
 * the wizard can tell the user exactly what did and did not land.
 */
import { db, describeDbError } from './db';
import type { EstimateWriteValues, LineItemWriteValues } from './estimateTypes';

export interface EstimateSaveResult {
  id: string;
  estimateNumber: string;
  /** Set when the estimate row saved but its line items did not. */
  lineItemWarning: string | null;
}

function withEstimateId(estimateId: string, items: LineItemWriteValues[]) {
  return items.map((item) => ({ ...item, estimate_id: estimateId }));
}

export async function createEstimate(
  values: EstimateWriteValues,
  items: LineItemWriteValues[],
): Promise<EstimateSaveResult> {
  const { data, error } = await db
    .from('estimates')
    .insert({ ...values, status: 'draft' })
    .select('id, estimate_number')
    .single();
  if (error || !data) {
    throw new Error(describeDbError(error, 'The estimate could not be saved.'));
  }
  const row = data as { id: string; estimate_number: string };

  let lineItemWarning: string | null = null;
  if (items.length > 0) {
    const { error: itemsError } = await db
      .from('estimate_line_items')
      .insert(withEstimateId(row.id, items));
    if (itemsError) {
      lineItemWarning = `Estimate ${row.estimate_number} was saved, but its line items were not: ${describeDbError(itemsError, 'unknown error')}. Open Edit to add them again.`;
    }
  }
  return { id: row.id, estimateNumber: row.estimate_number, lineItemWarning };
}

export async function updateEstimate(
  estimateId: string,
  values: EstimateWriteValues,
  items: LineItemWriteValues[],
): Promise<EstimateSaveResult> {
  const { data, error } = await db
    .from('estimates')
    .update(values)
    .eq('id', estimateId)
    .eq('status', 'draft')
    .select('id, estimate_number');
  if (error) throw new Error(describeDbError(error, 'The estimate could not be updated.'));
  const rows = (data ?? []) as Array<{ id: string; estimate_number: string }>;
  if (rows.length === 0) {
    throw new Error('Only draft estimates can be edited. This estimate is no longer a draft.');
  }
  const row = rows[0];

  const { error: deleteError } = await db
    .from('estimate_line_items')
    .delete()
    .eq('estimate_id', estimateId);
  if (deleteError) {
    throw new Error(
      `Estimate details were updated, but the previous line items could not be replaced: ${describeDbError(deleteError, 'unknown error')}`,
    );
  }

  if (items.length > 0) {
    const { error: itemsError } = await db
      .from('estimate_line_items')
      .insert(withEstimateId(estimateId, items));
    if (itemsError) {
      throw new Error(
        `Line items could not be saved after the previous ones were removed: ${describeDbError(itemsError, 'unknown error')}. Your entries are still in this form — save again to retry.`,
      );
    }
  }
  return { id: row.id, estimateNumber: row.estimate_number, lineItemWarning: null };
}
